import {
  addDays,
  addMonths,
  format,
  startOfWeek as getStartOfWeek,
  isSameDay,
  isSameMonth,
  startOfMonth,
} from 'date-fns';

export enum WeekDays {
  Sunday = 0,
  Monday = 1,
  Saturday = 6,
}

export const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Options for configuring calendar generation
 */
interface Options {
  startOfWeek?: WeekDays.Monday | WeekDays.Sunday;
  numberOfMonths?: number;
  startDate?: Date;
}

/**
 * Represents a single day in the calendar grid
 */
export interface CalendarDay {
  date: Date;
  /** Date string in YYYY-MM-DD format */
  dateString: string;
  /** Day number (1-31) */
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  /** Day of week (0-6, where 0 is Sunday) */
  dayOfWeek: number;
  /** Week number within the month (0-5) */
  weekOfMonth: number;
  /** Index of the month this day belongs to (0-based) */
  monthIndex: number;
}

/**
 * Represents a week containing 7 days
 */
export interface CalendarWeek {
  days: CalendarDay[];
  weekNumberInMonth: number;
  weekNumberInRange: number;
}

/**
 * Represents a month containing 4-6 weeks
 */
export interface CalendarMonth {
  /** string representing the first day of this month */
  monthDate: string;
  monthName: string;
  monthYear: number;
  /** Array of weeks in this month (4-6 weeks, excluding weeks with no days from current month) */
  weeks: CalendarWeek[];
  /** Index of this month in the calendar (0-based) */
  monthIndex: number;
  gridStart: string;
  gridEnd: string;
}

export interface CalendarMetadata {
  /** Array of months in the calendar */
  months: CalendarMonth[];
  /** Start of week setting used (0 for Sunday, 1 for Monday) */
  startOfWeek: Options['startOfWeek'];
  startDate: Date;
  endDate: Date;
}

/**
 * Factory class for generating calendar data structures optimized for grid-based layouts.
 *
 * This factory creates a comprehensive calendar data structure that can be easily
 * looped over to build grid-based calendars. Each month contains 4-6 weeks,
 * automatically excluding weeks that contain only days from other months.
 *
 * @example
 * ```typescript
 * // Basic usage
 * const calendar = CalendarFactory.createCalendar();
 *
 * // With custom options
 * const calendar = CalendarFactory.createCalendar({
 *   startOfWeek: 0, // Sunday
 *   numberOfMonths: 3,
 *   startDate: new Date('2023-06-01')
 * });
 *
 * // Loop in template
 * <!-- calendar.component.html -->
 * <div class="calendar">
 *   @for (month of calendar.months; track month.monthIndex) {
 *     <div class="month">
 *       <h2>{{ month.monthName }} {{ month.monthYear }}</h2>
 *       @for (week of month.weeks; track week.weekNumber) {
 *         <div class="week">
 *           @for (day of week.days; track day.dateString) {
 *             <button
 *               class="day"
 *               [class.current-month]="day.isCurrentMonth"
 *               [class.today]="day.isToday"
 *               [class.weekend]="day.isWeekend">
 *               {{ day.dayNumber }}
 *             </button>
 *           }
 *         </div>
 *       }
 *     </div>
 *   }
 * </div>
 * ```
 */
export class CalendarFactory {
  static createCalendar(options: Options = {}): CalendarMetadata {
    const { startOfWeek = WeekDays.Monday, numberOfMonths = 1, startDate = new Date() } = options;

    const months: CalendarMonth[] = [];
    const today = new Date();

    for (let monthIndex = 0; monthIndex < numberOfMonths; monthIndex++) {
      const currentMonthDate = addMonths(startOfMonth(startDate), monthIndex);

      // Calculate the first day of the grid (might be from previous month)
      const gridStart = getStartOfWeek(currentMonthDate, { weekStartsOn: startOfWeek });

      const weeks: CalendarWeek[] = [];
      let weekNumber = 0;

      const lastMonth = months[monthIndex - 1];
      let rangeWeekIndexStart = lastMonth?.weeks[lastMonth.weeks.length - 1]?.weekNumberInRange ?? 0;
      // Generate weeks, skipping those with no current month days

      for (let week = 0; week < 6; week++) {
        const days: CalendarDay[] = [];

        for (let day = 0; day < 7; day++) {
          const currentDate = addDays(gridStart, week * 7 + day);
          const dayOfWeek = currentDate.getDay();

          const calendarDay: CalendarDay = {
            date: currentDate,
            dateString: format(currentDate, DATE_FORMAT),
            dayNumber: currentDate.getDate(),
            isCurrentMonth: isSameMonth(currentDate, currentMonthDate),
            isToday: isSameDay(currentDate, today),
            isWeekend: dayOfWeek === WeekDays.Sunday || dayOfWeek === WeekDays.Saturday,
            dayOfWeek,
            weekOfMonth: weekNumber,
            monthIndex,
          };

          days.push(calendarDay);
        }

        // Only add the week if it contains at least one day from the current month
        if (days.some((day) => day.isCurrentMonth)) {
          weeks.push({
            days,
            weekNumberInMonth: weekNumber++,
            weekNumberInRange: rangeWeekIndexStart++, // This is the week number in the range of all weeks in the generated calendar
          });
        }
      }

      months.push({
        monthDate: format(currentMonthDate, DATE_FORMAT),
        monthName: format(currentMonthDate, 'MMMM'),
        monthYear: currentMonthDate.getFullYear(),
        weeks,
        monthIndex,
        gridStart: format(gridStart, DATE_FORMAT),
        gridEnd: format(weeks[weeks.length - 1].days[6].date, DATE_FORMAT),
      });
    }

    const lastMonth = months[months.length - 1];
    const endDate = lastMonth?.weeks[lastMonth.weeks.length - 1]?.days[6]?.date;

    return {
      months,
      startOfWeek,
      startDate: months[0]?.weeks[0]?.days[0]?.date || startDate,
      endDate,
    };
  }

  /**
   * Gets day labels (abbreviated day names) arranged according to start of week preference.
   */
  static getDayLabels(startOfWeek: WeekDays.Monday | WeekDays.Sunday = WeekDays.Monday): string[] {
    const shortLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    if (startOfWeek === WeekDays.Sunday) {
      return shortLabels;
    }

    // Monday first - rotate array
    return [...shortLabels.slice(1), shortLabels[0]];
  }
}
